import type * as THREE from 'three';
import type { PerfSnapshot } from '@/contracts';

export type { PerfSnapshot };

/**
 * Permanent frame-time instrument.
 *
 * Round 1 shipped with no frame time ever measured on real GPU hardware, which
 * made the spec's 60 fps / 30 fps floor unfalsifiable. This class exists so that
 * never happens again: it is always on, costs one ring-buffer write per frame,
 * and is readable from the perf overlay, the screenshot harness and the console
 * (`game.perf.snapshot()`).
 *
 * Three clocks are kept, because on a vsync-locked page only one of them is
 * honest about headroom:
 *
 *   wall — interval between `requestAnimationFrame` callbacks. This is what the
 *          player feels, but it clamps to the refresh interval, so a scene that
 *          could run at 300 fps and one that just barely makes 60 both read 16.7.
 *   cpu  — wall-clock spent inside the engine tick (sim + module updates +
 *          issuing the render). Never vsync-clamped, so it is the real CPU cost.
 *   gpu  — `EXT_disjoint_timer_query_webgl2` elapsed time around the render
 *          call. This is the number the budget table is actually about. Not all
 *          browsers expose the extension; `gpuSupported` says whether it is real.
 *
 * Percentiles rather than a mean: a 9 ms average with 40 ms hitches and a steady
 * 14 ms feel nothing alike in the hands, and only p99 tells them apart.
 */

/** Rolling window length. 240 frames is ~4 s at 60 Hz — long enough to catch a
 *  hitch, short enough that the numbers still track a camera move. */
const CAP = 240;

/** How many timer queries may be in flight before we stop issuing new ones. */
const GPU_QUEUE = 4;

type TimerExt = {
  TIME_ELAPSED_EXT: number;
  GPU_DISJOINT_EXT: number;
  QUERY_COUNTER_BITS_EXT: number;
};

export class FrameStats {
  private readonly wall = new Float32Array(CAP);
  private readonly cpu = new Float32Array(CAP);
  private readonly gpu = new Float32Array(CAP);
  private readonly scratch = new Float32Array(CAP);

  private wallN = 0;
  private cpuN = 0;
  private gpuN = 0;
  private wallCursor = 0;
  private cpuCursor = 0;
  private gpuCursor = 0;

  private tickStart = 0;
  private lastNow = 0;

  /** Light EMA kept for the auto-quality governor, which wants a smooth scalar. */
  private ema = 16.7;

  private calls = 0;
  private triangles = 0;
  private peakCalls = 0;
  private peakTriangles = 0;

  // --- GPU timer query state ------------------------------------------------
  private gl: WebGL2RenderingContext | null = null;
  private ext: TimerExt | null = null;
  private readonly pending: WebGLQuery[] = [];
  private readonly spare: WebGLQuery[] = [];
  private active: WebGLQuery | null = null;

  private readonly out: PerfSnapshot = {
    samples: 0, p50: 0, p95: 0, p99: 0, mean: 0, min: 0, max: 0, fps: 0,
    cpuP50: 0, cpuP99: 0, gpuP50: 0, gpuP99: 0, gpuSupported: false,
    calls: 0, triangles: 0, peakCalls: 0, peakTriangles: 0,
    programs: 0, geometries: 0, textures: 0,
  };

  /**
   * Bind to the live GL context so GPU timings become available. Safe to call
   * with a WebGL1 context or a browser without the extension — the instrument
   * simply reports `gpuSupported: false` and the other two clocks still work.
   */
  attach(renderer: THREE.WebGLRenderer): void {
    const gl = renderer.getContext() as WebGL2RenderingContext;
    if (typeof WebGL2RenderingContext === 'undefined' || !(gl instanceof WebGL2RenderingContext)) return;
    const ext = gl.getExtension('EXT_disjoint_timer_query_webgl2') as TimerExt | null;
    if (!ext) return;
    // A driver that reports zero counter bits accepts the queries and always
    // answers 0, which is worse than not measuring at all.
    const bits = gl.getQuery(ext.TIME_ELAPSED_EXT, ext.QUERY_COUNTER_BITS_EXT) as number | null;
    if (typeof bits === 'number' && bits === 0) return;
    this.gl = gl;
    this.ext = ext;
  }

  get gpuSupported(): boolean { return this.ext !== null; }
  get frameMs(): number { return this.ema; }

  /** Call at the very top of the frame, with the rAF timestamp. */
  beginFrame(now: number): void {
    if (this.lastNow > 0) {
      const dt = now - this.lastNow;
      // A tab-switch or breakpoint produces a multi-second interval that would
      // dominate p99 forever. Anything past a third of a second is a stall, not
      // a frame, and is dropped from the distribution.
      if (dt < 333) {
        this.wall[this.wallCursor] = dt;
        this.wallCursor = (this.wallCursor + 1) % CAP;
        if (this.wallN < CAP) this.wallN++;
        this.ema += (dt - this.ema) * 0.1;
      }
    }
    this.lastNow = now;
    this.tickStart = performance.now();
    this.beginGpuQuery();
  }

  /** Call after the render has been issued, before returning from the tick. */
  endFrame(info: THREE.WebGLRenderer['info']): void {
    const ms = performance.now() - this.tickStart;
    this.cpu[this.cpuCursor] = ms;
    this.cpuCursor = (this.cpuCursor + 1) % CAP;
    if (this.cpuN < CAP) this.cpuN++;

    this.calls = info.render.calls;
    this.triangles = info.render.triangles;
    if (this.calls > this.peakCalls) this.peakCalls = this.calls;
    if (this.triangles > this.peakTriangles) this.peakTriangles = this.triangles;

    this.endGpuQuery();
    this.drainGpuQueries();
  }

  /** Clear the rolling window. The harness calls this after each camera move. */
  reset(): void {
    this.wallN = 0; this.cpuN = 0; this.gpuN = 0;
    this.wallCursor = 0; this.cpuCursor = 0; this.gpuCursor = 0;
    this.lastNow = 0;
    this.peakCalls = 0;
    this.peakTriangles = 0;
  }

  /**
   * Percentiles over the current window. Sorts a scratch copy, so this is a few
   * microseconds — fine at the overlay's 4 Hz, not something to call per frame.
   */
  snapshot(info?: THREE.WebGLRenderer['info']): PerfSnapshot {
    const o = this.out;
    o.samples = this.wallN;
    o.p50 = this.pct(this.wall, this.wallN, 0.50);
    o.p95 = this.pct(this.wall, this.wallN, 0.95);
    o.p99 = this.pct(this.wall, this.wallN, 0.99);
    o.min = this.pct(this.wall, this.wallN, 0);
    o.max = this.pct(this.wall, this.wallN, 1);
    o.mean = this.avg(this.wall, this.wallN);
    o.fps = o.p50 > 0 ? 1000 / o.p50 : 0;
    o.cpuP50 = this.pct(this.cpu, this.cpuN, 0.50);
    o.cpuP99 = this.pct(this.cpu, this.cpuN, 0.99);
    o.gpuP50 = this.pct(this.gpu, this.gpuN, 0.50);
    o.gpuP99 = this.pct(this.gpu, this.gpuN, 0.99);
    o.gpuSupported = this.ext !== null && this.gpuN > 0;
    o.calls = this.calls;
    o.triangles = this.triangles;
    o.peakCalls = this.peakCalls;
    o.peakTriangles = this.peakTriangles;
    o.programs = info?.programs?.length ?? 0;
    o.geometries = info?.memory.geometries ?? 0;
    o.textures = info?.memory.textures ?? 0;
    return o;
  }

  dispose(): void {
    const gl = this.gl;
    if (!gl) return;
    for (const q of this.pending) gl.deleteQuery(q);
    for (const q of this.spare) gl.deleteQuery(q);
    if (this.active) gl.deleteQuery(this.active);
    this.pending.length = 0;
    this.spare.length = 0;
    this.active = null;
    this.gl = null;
    this.ext = null;
  }

  // --- internals ------------------------------------------------------------

  private pct(buf: Float32Array, n: number, q: number): number {
    if (n === 0) return 0;
    const s = this.scratch.subarray(0, n);
    s.set(buf.subarray(0, n));
    s.sort();
    const i = Math.min(n - 1, Math.max(0, Math.round(q * (n - 1))));
    return s[i];
  }

  private avg(buf: Float32Array, n: number): number {
    if (n === 0) return 0;
    let sum = 0;
    for (let i = 0; i < n; i++) sum += buf[i];
    return sum / n;
  }

  private beginGpuQuery(): void {
    const gl = this.gl;
    const ext = this.ext;
    if (!gl || !ext || this.active) return;
    if (this.pending.length >= GPU_QUEUE) return;
    const q = this.spare.pop() ?? gl.createQuery();
    if (!q) return;
    gl.beginQuery(ext.TIME_ELAPSED_EXT, q);
    this.active = q;
  }

  private endGpuQuery(): void {
    const gl = this.gl;
    const ext = this.ext;
    if (!gl || !ext || !this.active) return;
    gl.endQuery(ext.TIME_ELAPSED_EXT);
    this.pending.push(this.active);
    this.active = null;
  }

  private drainGpuQueries(): void {
    const gl = this.gl;
    const ext = this.ext;
    if (!gl || !ext || this.pending.length === 0) return;
    // A disjoint event means the GPU was preempted and every in-flight result is
    // garbage; throw the whole queue away rather than record a fictional spike.
    if (gl.getParameter(ext.GPU_DISJOINT_EXT)) {
      for (const q of this.pending) this.spare.push(q);
      this.pending.length = 0;
      return;
    }
    while (this.pending.length > 0) {
      const q = this.pending[0];
      if (!gl.getQueryParameter(q, gl.QUERY_RESULT_AVAILABLE)) break;
      const ns = gl.getQueryParameter(q, gl.QUERY_RESULT) as number;
      this.pending.shift();
      this.spare.push(q);
      this.gpu[this.gpuCursor] = ns / 1e6;
      this.gpuCursor = (this.gpuCursor + 1) % CAP;
      if (this.gpuN < CAP) this.gpuN++;
    }
  }
}
